import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  useToast,
  useColorModeValue,
  IconButton,
  Progress,
  FormControl,
  FormLabel,
  Center
} from '@chakra-ui/react';
import { FiArrowLeft, FiUpload, FiX, FiPlus, FiVideo, FiVolume2, FiPlay, FiFileText } from 'react-icons/fi';
import IconWrapper from '../components/IconWrapper';
import { useAuth } from '../contexts/AuthContext';
import { uploadFileDirect, createContentDocumentDirect } from '../services/uploadService';
import { useNavigate, useParams } from 'react-router-dom';
import { getContent, getNextEpisodeNumber } from '../services/contentService';

interface EpisodeFormData {
  episodeName: string;
  video: File | null;
  audio: File | null;
  trailer: File | null;
  subtitle: File | null;
  videoPreview: string | null;
  audioPreview: string | null;
  trailerPreview: string | null;
  subtitlePreview: string | null;
}

const UploadEpisodeNew: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { seriesId, seasonId } = useParams<{ seriesId: string; seasonId: string }>();
  const toast = useToast();
  
  const [formData, setFormData] = useState<EpisodeFormData>({
    episodeName: '',
    video: null,
    audio: null,
    trailer: null,
    subtitle: null,
    videoPreview: null,
    audioPreview: null,
    trailerPreview: null,
    subtitlePreview: null
  });
  
  const [series, setSeries] = useState<any>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const bgColor = useColorModeValue('white', 'gray.800');
  const inputBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const labelColor = useColorModeValue('gray.600', 'gray.400');

  const loadSeries = useCallback(async () => {
    try {
      const seriesData = await getContent('series', 1);
      const foundSeries = seriesData.find(s => s.id === seriesId);
      if (foundSeries) {
        setSeries(foundSeries);
      }
    } catch (error) {
      console.error('Error loading series:', error);
      toast({
        title: 'Error',
        description: 'Failed to load series information.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [seriesId, toast]);

  useEffect(() => {
    if (seriesId) {
      loadSeries();
    }
  }, [seriesId, loadSeries]);

  const handleInputChange = (field: keyof EpisodeFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileSelect = (type: 'video' | 'audio' | 'trailer' | 'subtitle', event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type based on category
      const validTypes = {
        video: ['video/'],
        audio: ['audio/'],
        trailer: ['video/'],
        subtitle: ['text/', 'application/', ''] // Allow empty MIME type for .srt files
      };

      // Special handling for subtitle files
      let isValidType = false;
      if (type === 'subtitle') {
        // Check MIME type or file extension for subtitles
        const validMimeTypes = ['text/', 'application/', 'text/plain', 'application/octet-stream'];
        const validExtensions = ['.srt', '.vtt', '.txt', '.sub'];
        const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
        
        isValidType = validMimeTypes.some(mimeType => file.type.startsWith(mimeType)) ||
                     validExtensions.includes(fileExtension) ||
                     file.type === ''; // Some systems don't set MIME type for .srt files
      } else {
        isValidType = validTypes[type].some(validType => file.type.startsWith(validType));
      }

      if (!isValidType) {
        toast({
          title: 'Invalid file type',
          description: `Please select a valid ${type} file. Supported formats: ${type === 'subtitle' ? '.srt, .vtt, .txt' : type === 'video' ? 'video files' : type === 'audio' ? 'audio files' : 'video files'}.`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Validate file size (max 8GB for videos, 100MB for others)
      const maxSize = type === 'video' || type === 'trailer' ? 8 * 1024 * 1024 * 1024 : 100 * 1024 * 1024;
      if (file.size > maxSize) {
        toast({
          title: 'File too large',
          description: `Please select a ${type} file smaller than ${type === 'video' || type === 'trailer' ? '8GB' : '100MB'}.`,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const previewUrl = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        [type]: file,
        [`${type}Preview`]: previewUrl
      }));
    }
  };

  const handleFileRemove = (type: 'video' | 'audio' | 'trailer' | 'subtitle') => {
    const previewField = `${type}Preview` as keyof EpisodeFormData;
    const previewUrl = formData[previewField] as string;
    
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    
    setFormData(prev => ({
      ...prev,
      [type]: null,
      [previewField]: null
    }));
  };

  const handleSubmit = async () => {
    if (!user?.uid || !seriesId) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to upload episodes.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.episodeName.trim() || !formData.video) {
      toast({
        title: 'Required fields missing',
        description: 'Please enter episode name and upload a video.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      // Upload video
      const videoUrl = await uploadFileDirect(`episodes/${user.uid}/${seriesId}`, formData.video);
      setUploadProgress(25);

      // Upload audio if provided
      let audioUrl = '';
      if (formData.audio) {
        audioUrl = await uploadFileDirect(`episodes/${user.uid}/${seriesId}/audio`, formData.audio);
        setUploadProgress(50);
      }

      // Upload trailer if provided
      let trailerUrl = '';
      if (formData.trailer) {
        trailerUrl = await uploadFileDirect(`episodes/${user.uid}/${seriesId}/trailers`, formData.trailer);
        setUploadProgress(75);
      }

      // Upload subtitle if provided
      let subtitleUrl = '';
      if (formData.subtitle) {
        subtitleUrl = await uploadFileDirect(`episodes/${user.uid}/${seriesId}/subtitles`, formData.subtitle);
        setUploadProgress(90);
      }

      // Resolve season number from seasonId param using local storage as temporary source
      let resolvedSeasonNumber = '1';
      const seasonsKey = `seasons-${seriesId}`;
      const savedSeasons = JSON.parse(localStorage.getItem(seasonsKey) || '[]');
      if (seasonId && savedSeasons && Array.isArray(savedSeasons)) {
        const found = savedSeasons.find((s: any) => s.id === seasonId);
        if (found?.seasonNumber) {
          resolvedSeasonNumber = String(found.seasonNumber);
        }
      }

      // Compute next numeric episode number for this season
      const nextEpisodeNum = await getNextEpisodeNumber(seriesId, resolvedSeasonNumber);

      // Create episode document
      const episodeData = {
        title: formData.episodeName,
        description: `Episode: ${formData.episodeName}`,
        category: 'episode',
        visibility: 'public',
        rating: 'All Ages',
        language: 'English',
        genres: series?.genres || [],
        linkedContent: seriesId,
        seasonNumber: resolvedSeasonNumber,
        episodeNumber: String(nextEpisodeNum),
        chapterNumber: ''
      };

      const assetUrls: { [key: string]: string } = {
        videoFile: videoUrl
      };
      if (audioUrl) assetUrls.audioFile = audioUrl;
      if (trailerUrl) assetUrls.trailerFile = trailerUrl;
      if (subtitleUrl) assetUrls.subtitleFile = subtitleUrl;

      await createContentDocumentDirect(user.uid, episodeData, assetUrls);
      
      // Save episode to localStorage for immediate display
      const episodeInfo = {
        id: `episode-${Date.now()}`,
        title: formData.episodeName,
        episodeNumber: String(nextEpisodeNum),
        thumbnail: videoUrl, // Use video URL as thumbnail for now
        duration: 'Unknown', // Could calculate actual duration
        description: `Episode: ${formData.episodeName}`,
        videoUrl: videoUrl,
        audioUrl: audioUrl,
        trailerUrl: trailerUrl,
        subtitleUrl: subtitleUrl,
        seriesId: seriesId,
        seasonId: seasonId || 'season-1'
      };
      
      // Save to localStorage
      const existingEpisodes = JSON.parse(localStorage.getItem(`episodes-${seriesId}`) || '[]');
      const updatedEpisodes = [...existingEpisodes, episodeInfo];
      localStorage.setItem(`episodes-${seriesId}`, JSON.stringify(updatedEpisodes));
      
      // Update season episode count
      const existingSeasons = JSON.parse(localStorage.getItem(seasonsKey) || '[]');
      const updatedSeasons = existingSeasons.map((season: any) => {
        if (season.id === seasonId) {
          return { ...season, episodeCount: season.episodeCount + 1 };
        }
        return season;
      });
      localStorage.setItem(seasonsKey, JSON.stringify(updatedSeasons));
      
      setUploadProgress(100);

      // Refresh seasons data to update episode counts
      if ((window as any).refreshSeasons) {
        (window as any).refreshSeasons();
      }
      
      // Refresh episodes data
      if ((window as any).refreshEpisodes) {
        (window as any).refreshEpisodes();
      }

      toast({
        title: 'Episode uploaded successfully!',
        description: 'Your episode has been added to the series.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      // Navigate back to series management
      navigate(`/series/${seriesId}/manage`);
    } catch (error) {
      console.error('Error uploading episode:', error);
      toast({
        title: 'Upload failed',
        description: 'There was an error uploading your episode. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderFileUpload = (
    type: 'video' | 'audio' | 'trailer' | 'subtitle',
    label: string,
    icon: React.ReactElement,
    buttonText: string
  ) => {
    const file = formData[type];
    const hasFile = !!file;

    return (
      <FormControl>
        <FormLabel color={labelColor} fontWeight="bold" fontSize="md">
          {label}
        </FormLabel>
        <HStack gap={2}>
          <Button
            flex="1"
            variant={hasFile ? 'solid' : 'outline'}
            colorScheme="purple"
            onClick={() => document.getElementById(`${type}-upload`)?.click()}
            leftIcon={icon}
            h="60px"
          >
            {hasFile ? file?.name : buttonText}
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="purple"
            onClick={() => document.getElementById(`${type}-upload`)?.click()}
            minW="60px"
            h="60px"
          >
            <IconWrapper icon={FiPlus} size={20} />
          </Button>
          <Input
            id={`${type}-upload`}
            type="file"
            accept={type === 'video' || type === 'trailer' ? 'video/*' : type === 'audio' ? 'audio/*' : '.srt,.vtt,.txt,.sub,text/plain,application/octet-stream'}
            onChange={(e) => handleFileSelect(type, e)}
            display="none"
          />
        </HStack>
        {hasFile && (
          <HStack mt={2}>
            <Text fontSize="sm" color="gray.500">
              {file?.name} ({(file?.size! / (1024 * 1024)).toFixed(1)} MB)
            </Text>
            <Button
              size="xs"
              variant="ghost"
              colorScheme="red"
              onClick={() => handleFileRemove(type)}
            >
              <IconWrapper icon={FiX} size={12} />
            </Button>
          </HStack>
        )}
      </FormControl>
    );
  };

  if (!user) {
    return (
      <Center h="50vh">
        <VStack gap={4}>
          <Text fontSize="lg" color="gray.500">
            Please log in to upload episodes.
          </Text>
          <Button onClick={() => navigate('/login')} colorScheme="purple">
            Log In
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box minH="100vh" bg={bgColor}>
      {/* Header */}
      <Box p={4} borderBottom="1px solid" borderColor={borderColor}>
        <HStack gap={4} align="center">
          <IconButton
            aria-label="Go back"
            icon={<IconWrapper icon={FiArrowLeft} size={20} />}
            variant="ghost"
            onClick={() => navigate(-1)}
            size="sm"
            bg="purple.100"
            _hover={{ bg: 'purple.200' }}
          />
          <Text fontSize="xl" fontWeight="bold" color={textColor}>
            Upload Episode
          </Text>
          <Text fontSize="sm" color="gray.500" ml="auto">
            5/5
          </Text>
        </HStack>
      </Box>

      {/* Main Content */}
      <Box p={6} maxW="md" mx="auto">
        <VStack spacing={6} align="stretch">
          {/* Episode Name */}
          <FormControl>
            <FormLabel color={labelColor} fontWeight="bold" fontSize="md">
              Episode Name
            </FormLabel>
            <Input
              value={formData.episodeName}
              onChange={(e) => handleInputChange('episodeName', e.target.value)}
              placeholder="Add Episode Number or name"
              bg={inputBg}
              borderColor={borderColor}
              _hover={{ borderColor: 'purple.300' }}
              _focus={{ borderColor: 'purple.500', boxShadow: '0 0 0 1px purple.500' }}
            />
          </FormControl>

          {/* Upload Video */}
          {renderFileUpload('video', 'Upload Video', <IconWrapper icon={FiVideo} size={20} />, 'Add Video')}

          {/* Upload Audio */}
          {renderFileUpload('audio', 'Upload Audio (Optional)', <IconWrapper icon={FiVolume2} size={20} />, 'Add Audio (Optional)')}

          {/* Upload Trailer */}
          {renderFileUpload('trailer', 'Upload Trailer', <IconWrapper icon={FiPlay} size={20} />, 'Add Trailer')}

          {/* Upload Subtitle */}
          {renderFileUpload('subtitle', 'Upload Subtitle', <IconWrapper icon={FiFileText} size={20} />, 'Add Subtitle')}

          {/* Progress Bar */}
          {isUploading && (
            <VStack gap={2}>
              <Text fontSize="sm" color={labelColor}>
                Uploading... {Math.round(uploadProgress)}%
              </Text>
              <Progress
                value={uploadProgress}
                size="sm"
                colorScheme="purple"
                w="100%"
                borderRadius="full"
              />
            </VStack>
          )}

          {/* Upload Button */}
          <Button
            colorScheme="purple"
            size="lg"
            onClick={handleSubmit}
            isLoading={isUploading}
            loadingText="Uploading..."
            isDisabled={!formData.episodeName.trim() || !formData.video}
            leftIcon={<IconWrapper icon={FiUpload} size={20} />}
            w="100%"
            h="60px"
          >
            Upload
          </Button>
        </VStack>
      </Box>
    </Box>
  );
};

export default UploadEpisodeNew;
